import { useState, useMemo, useCallback, useEffect } from "react";
import { format } from "date-fns";
import { Outlet, useNavigate } from "react-router-dom";

import { PageBreadcrumb } from "@/layouts/page-container/shared/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Clock,
  DollarSign,
  Image as ImageIcon,
  CalendarCheck,
  FileText,
  Grid,
  Users,
  Maximize2,
  StickyNote,
  Video,
  Pencil,
  DoorClosed,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/use-toast";
import { privateOfficeService } from "@/services/private-office-service";
import {
  PrivateOfficeUpdateStatus,
  PrivateOfficeUpdateField,
  PrivateOfficeUpdateRequest,
  GroupedUpdateRequest,
  GetPrivateOfficeUpdateRequestsResponse,
} from "@/services/private-office-service/types";
import { ImageRequestHandler } from "@/features/private-office/layouts/image-requests/image-request-handler";
import {
  isImageRequest,
  IMAGE_PROPERTIES,
  extractImagesFromOfficeInfo,
} from "@/utils/image-utils";
import {
  PictureProperty,
  CurrentRoomImages,
} from "@/services/private-office-service/types";
import { printPrice } from "@/utils/print-price";
import { Pagination } from "@/features/pagination/layouts/pagination/pagination";
import { PRIVATE_OFFICE_STATUS_LABELS } from "@/@types/private-office.types";
import { useAuth } from "@/features/authentication/context/auth/auth";
import { PhotoItem } from "@/features/private-room/photos/adapters/get-room-photos/get-room-photos.types";
import { useFilters } from "@/hooks/use-filters";
import { extractApiErrorMessage } from "@/utils/extract-api-error-message";
import { Controller, useForm } from "react-hook-form";
import { SearchInputCommand } from "@/components/inputs/search-input-command";

type StatusFilter = "all" | "pending" | "approved" | "rejected";

export const PrivateOfficeUpdateRequests = () => {
  const navigate = useNavigate();
  const [expandedRooms, setExpandedRooms] = useState<Set<number>>(new Set());
  const [expandedRequests, setExpandedRequests] = useState<Set<string>>(
    new Set(),
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [spaceFilter, setSpaceFilter] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isLoadingApproving, setLoadingApproving] = useState(false);
  const [isLoadingRejecting, setLoadingRejecting] = useState(false);
  const [updateRequestsData, setUpdateRequestsData] =
    useState<GetPrivateOfficeUpdateRequestsResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [fullSizeImage, setFullSizeImage] = useState<{
    src: string;
    alt: string;
  } | null>(null);
  // Slots already written by approvals made in this session, per room. Each
  // request carries its own `office_info` snapshot frozen at creation time, so
  // sibling requests of the same room would otherwise keep offering a slot that
  // a previous approval has just filled — and the second approval would
  // overwrite the first photo.
  const [appliedImageSlots, setAppliedImageSlots] = useState<
    Record<number, Partial<CurrentRoomImages>>
  >({});
  const { userInfo } = useAuth();
  const { handSelectedSpace, filterSpaces } = useFilters();
  const { control, setValue } = useForm();

  const pageLimit = 20;

  // Fetch update requests
  const fetchUpdateRequests = useCallback(
    async (
      page: number,
      status: StatusFilter,
      spaceData?: { id: number; name: string } | null,
    ) => {
      try {
        setIsLoadingContent(true);

        const params: {
          page: number;
          limit: number;
          status?: "pending" | "approved" | "rejected";
          space_name?: string;
        } = {
          page,
          limit: pageLimit,
        };

        if (status !== "all") {
          params.status = status;
        }

        if (spaceData && spaceData.name) {
          params.space_name = spaceData.name;
        }

        const response = await privateOfficeService.getUpdateRequests(params);
        setUpdateRequestsData(response);
      } catch (error) {
        console.error("Error fetching update requests:", error);
        toast({
          title: "Erro",
          description: "Erro ao carregar solicitações de alteração",
          variant: "destructive",
        });
      } finally {
        setIsLoadingContent(false);
      }
    },
    [toast],
  );

  // Handle filter change - reset page and let the main effect handle the fetch
  const handleFilterChange = useCallback(
    (newFilter: StatusFilter) => {
      setStatusFilter(newFilter);
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    },
    [currentPage],
  );

  // Clear all filters without changing page
  const handleClearAllFilters = useCallback(() => {
    setStatusFilter("all");
    setSpaceFilter(null);
    setValue("spaces", null);
  }, [setValue]);

  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceFilter]);

  useEffect(() => {
    fetchUpdateRequests(currentPage, statusFilter, spaceFilter);

    const handleRefresh = () => {
      fetchUpdateRequests(currentPage, statusFilter, spaceFilter);
    };

    window.addEventListener("updateRequestsRefresh", handleRefresh);
    return () =>
      window.removeEventListener("updateRequestsRefresh", handleRefresh);
  }, [currentPage, statusFilter, spaceFilter, fetchUpdateRequests]);

  const handlePageClick = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleNextPageClick = useCallback(() => {
    if (
      updateRequestsData?.pagination &&
      currentPage < updateRequestsData.pagination.totalPages
    ) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, updateRequestsData?.pagination]);

  const handlePrevPageClick = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  const groupedRequests = useMemo(() => {
    if (!updateRequestsData?.data) return [];
    return updateRequestsData.data;
  }, [updateRequestsData?.data]);

  // Toggle room expansion
  const toggleRoomExpansion = useCallback((roomId: number) => {
    setExpandedRooms((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(roomId)) {
        newSet.delete(roomId);
      } else {
        newSet.add(roomId);
      }
      return newSet;
    });
  }, []);

  // Transform inventory office metadata to field format for display
  const transformInventoryOfficeToFields = useCallback((metadata: any) => {
    const fields: Record<string, any> = {};

    if (metadata.name) fields.name = metadata.name;
    if (metadata.sizeM2) fields.size = metadata.sizeM2;
    if (metadata.capacity) fields.capacity = metadata.capacity;
    if (metadata.monthlyPrice) fields.monthly_price = metadata.monthlyPrice;
    if (metadata.description) fields.description = metadata.description;
    if (metadata.notes) fields.notes = metadata.notes;
    if (metadata.status) fields.status = metadata.status;
    // approvalStatus is excluded from display as it's already shown in the header
    if (metadata.photos && metadata.photos.length > 0) {
      fields.photos = metadata.photos;
    }
    if (metadata.amenities && metadata.amenities.length > 0) {
      // Transform amenities to a summary format
      const amenitiesText = metadata.amenities
        .map((category: any) =>
          category.amenities.map((amenity: any) => amenity.name).join(", "),
        )
        .join("; ");
      fields.amenities = amenitiesText;
    }

    return fields;
  }, []);

  // Toggle request expansion
  const toggleRequestExpansion = useCallback((requestId: string) => {
    setExpandedRequests((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  }, []);

  // Handle single approve
  const handleApprove = useCallback(
    async (requestId: string) => {
      setIsApproving(true);
      setLoadingApproving(true);
      try {
        const data = await privateOfficeService.approveUpdateRequest(
          requestId,
          {
            userEmail: userInfo.profile?.email || "",
          },
        );
        updateDataAfterRequestChanged(data);
        toast({
          title: "Sucesso",
          description: "Alteração aprovada com sucesso",
        });
      } catch (error) {
        console.error("Error approving request:", error);
        toast({
          title: "Erro",
          description: extractApiErrorMessage(
            error,
            "Erro ao aprovar alteração",
          ),
          variant: "destructive",
        });
      } finally {
        setIsApproving(false);
        setLoadingApproving(false);
      }
    },
    [updateRequestsData, currentPage, statusFilter, userInfo?.profile?.email],
  );

  // Handle single reject
  const handleReject = useCallback(
    async (requestId: string) => {
      setIsApproving(true);
      setLoadingRejecting(true);
      try {
        const data = await privateOfficeService.rejectUpdateRequest(
          requestId,
          `Rejected by ${userInfo.profile?.email}`,
        );
        updateDataAfterRequestChanged(data);
        toast({
          title: "Sucesso",
          description: "Alteração rejeitada com sucesso",
        });
      } catch (error) {
        console.error("Error rejecting request:", error);
        toast({
          title: "Erro",
          description: extractApiErrorMessage(
            error,
            "Erro ao rejeitar alteração",
          ),
          variant: "destructive",
        });
      } finally {
        setIsApproving(false);
        setLoadingRejecting(false);
      }
    },
    [updateRequestsData, currentPage, statusFilter, userInfo?.profile?.email],
  );

  // Image modal handlers
  const closeImageModal = useCallback(() => {
    setFullSizeImage(null);
  }, []);

  // Check if field is a picture field
  // Helper function to format operating hours for display
  const formatOperatingHours = useCallback((operatingHours: any): string => {
    if (!operatingHours) return "Sem horário definido";

    try {
      const hours =
        typeof operatingHours === "string"
          ? JSON.parse(operatingHours)
          : operatingHours;

      const dayNames: Record<string, string> = {
        monday: "Segunda",
        tuesday: "Terça",
        wednesday: "Quarta",
        thursday: "Quinta",
        friday: "Sexta",
        saturday: "Sábado",
        sunday: "Domingo",
      };

      const schedule: { day: string; slots: any[] }[] = [];
      const allDays = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];

      for (const day of allDays) {
        const slots = hours[day];
        if (slots && Array.isArray(slots) && slots.length > 0) {
          schedule.push({ day: dayNames[day], slots });
        }
      }

      if (schedule.length === 0) return "Sem horário definido";

      // Check if all days have the same schedule
      const firstSlots = JSON.stringify(schedule[0].slots);
      const allSame =
        schedule.length === 7 &&
        schedule.every((s) => JSON.stringify(s.slots) === firstSlots);

      if (allSame) {
        const slot = schedule[0].slots[0];
        if (slot.open === "00:00" && slot.close === "23:59") {
          return "24 horas, todos os dias";
        }
        return `Todos os dias: ${slot.open} às ${slot.close}`;
      }

      // Group consecutive days with same schedule
      const groups: { days: string[]; slots: any[] }[] = [];
      let currentGroup = { days: [schedule[0].day], slots: schedule[0].slots };

      for (let i = 1; i < schedule.length; i++) {
        if (
          JSON.stringify(schedule[i].slots) ===
          JSON.stringify(currentGroup.slots)
        ) {
          currentGroup.days.push(schedule[i].day);
        } else {
          groups.push({ ...currentGroup });
          currentGroup = { days: [schedule[i].day], slots: schedule[i].slots };
        }
      }
      groups.push(currentGroup);

      return groups
        .map((group) => {
          const dayRange =
            group.days.length > 1
              ? `${group.days[0]} a ${group.days[group.days.length - 1]}`
              : group.days[0];
          const timeSlot = group.slots[0];
          return `${dayRange}: ${timeSlot.open} às ${timeSlot.close}`;
        })
        .join("; ");
    } catch (error) {
      console.error("Error formatting operating hours:", error);
      return "Formato inválido";
    }
  }, []);

  const isPictureField = useCallback((field: PrivateOfficeUpdateField) => {
    return (
      field.hero_image ||
      field.cover_photo ||
      field.gallery_images ||
      field.amenity_icons
    );
  }, []);

  // Get field display information
  const getFieldDisplay = useCallback(
    (
      field: PrivateOfficeUpdateField,
      request?: PrivateOfficeUpdateRequest,
      isProposed: boolean = true,
    ) => {
      // Special handling for amenities
      if (field.amenities !== undefined) {
        if (isProposed && request?.additional_data?.amenities_summary) {
          // For proposed values, show summary
          return {
            name: "Comodidades",
            value: request.additional_data.amenities_summary.summary,
            isPicture: false,
          };
        } else if (!isProposed) {
          // For previous values (ATUAL), return null to show "Sem valor anterior"
          return null;
        }
      }

      // Special handling for operatingHours (support both camelCase and snake_case)
      if (
        field.operatingHours !== undefined ||
        (field as any).operating_hours !== undefined
      ) {
        if (isProposed && request?.additional_data?.operating_hours_summary) {
          // For proposed values, show summary from backend
          return {
            name: "Horário de Funcionamento",
            value: request.additional_data.operating_hours_summary,
            isPicture: false,
          };
        } else if (!isProposed) {
          // For previous values (ATUAL), format the previous operating hours
          const previousValue =
            field.operatingHours || (field as any).operating_hours;
          if (previousValue) {
            return {
              name: "Horário de Funcionamento",
              value: formatOperatingHours(previousValue),
              isPicture: false,
            };
          }
          return null;
        }
      }

      const fieldMapping: Record<string, { name: string; isPicture: boolean }> =
        {
          name: { name: "Nome", isPicture: false },
          label: { name: "Nome", isPicture: false },
          description: { name: "Descrição", isPicture: false },
          operatingHours: {
            name: "Horário de Funcionamento",
            isPicture: false,
          },
          operating_hours: {
            name: "Horário de Funcionamento",
            isPicture: false,
          },
          note: { name: "Observações", isPicture: false },
          notes: { name: "Observações", isPicture: false },
          price: { name: "Preço", isPicture: false },
          monthly_price: { name: "Preço Mensal", isPicture: false },
          monthlyPrice: { name: "Preço Mensal", isPicture: false },
          yearly_price: { name: "Preço Anual", isPicture: false },
          yearlyPrice: { name: "Preço Anual", isPicture: false },
          hero_image: { name: "Imagem Principal", isPicture: true },
          cover_photo: { name: "Foto de Capa", isPicture: true },
          gallery_images: { name: "Galeria de Imagens", isPicture: true },
          pictures: { name: "Fotos", isPicture: true },
          photos: { name: "Fotos", isPicture: true },
          amenity_icons: { name: "Ícones de Comodidades", isPicture: true },
          capacity: { name: "Capacidade", isPicture: false },
          area: { name: "Área", isPicture: false },
          size: { name: "Área", isPicture: false },
          floor: { name: "Andar", isPicture: false },
          status: { name: "Disponibilidade", isPicture: false },
          guarantee_fee: { name: "Taxa de Garantia", isPicture: false },
          guaranteeFee: { name: "Taxa de Garantia", isPicture: false },
          amenities: { name: "Comodidades", isPicture: false },
          approvalStatus: { name: "Criação da sala", isPicture: false },
        };

      // Find the first non-null, non-undefined value in the field object
      const fieldEntries = Object.entries(field);

      for (const [key, value] of fieldEntries) {
        // Skip undefined, null, and empty string values
        if (value === undefined || value === null || value === "") {
          continue;
        }

        const mapping = fieldMapping[key] || {
          name: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
          isPicture:
            key.includes("image") ||
            key.includes("photo") ||
            key.includes("picture") ||
            key.includes("gallery"),
        };

        let displayValue: string;

        if (typeof value === "object") {
          if (Array.isArray(value)) {
            // Special handling for photos array
            if (key === "photos" && value.length > 0 && value[0]?.url) {
              displayValue = `${value.length} foto${value.length > 1 ? "s" : ""}`;
            } else {
              displayValue =
                value.length > 0 ? value.join(", ") : "Lista vazia";
            }
          } else {
            displayValue = JSON.stringify(value, null, 2);
          }
        } else if (
          key.includes("price") ||
          key === "guarantee_fee" ||
          key === "guaranteeFee"
        ) {
          const numValue = Number(value);
          displayValue = isNaN(numValue) ? String(value) : printPrice(numValue);
        } else if (key === "status") {
          const statusString = value as string;
          displayValue =
            PRIVATE_OFFICE_STATUS_LABELS[
              statusString as keyof typeof PRIVATE_OFFICE_STATUS_LABELS
            ] || String(value);
        } else {
          displayValue = String(value);
        }

        return {
          name: mapping.name,
          value: displayValue,
          isPicture: mapping.isPicture,
        };
      }

      // Fallback: if no valid field found, try to show all available fields for debug
      const availableFields = Object.keys(field).join(", ");
      return {
        name: "Campo",
        value: availableFields || "Nenhum campo disponível",
        isPicture: false,
      };
    },
    [isPictureField, formatOperatingHours],
  );

  // Get change types for a group of requests
  const getChangeTypes = useCallback(
    (requests: GroupedUpdateRequest["requests"]) => {
      const changeTypes = new Set<string>();

      requests.forEach((request) => {
        const fieldDisplay = getFieldDisplay(request.fields_to_update, request);
        const fieldEntries = Object.entries(request.fields_to_update);

        for (const [key] of fieldEntries) {
          if (
            key.includes("price") ||
            key === "guarantee_fee" ||
            key === "guaranteeFee"
          ) {
            changeTypes.add("price");
          } else if (key === "status") {
            changeTypes.add("status");
          } else if (key === "approvalStatus") {
            changeTypes.add("approvalStatus");
          } else if (
            fieldDisplay?.isPicture ||
            IMAGE_PROPERTIES.includes(key as PictureProperty)
          ) {
            changeTypes.add("image");
          } else if (key === "name" || key === "label") {
            changeTypes.add("name");
          } else if (key === "description") {
            changeTypes.add("description");
          } else if (key === "amenities") {
            changeTypes.add("amenities");
          } else if (key === "operatingHours" || key === "operating_hours") {
            changeTypes.add("schedule");
          } else if (key === "capacity") {
            changeTypes.add("capacity");
          } else if (key === "size" || key === "area") {
            changeTypes.add("area");
          } else if (key === "notes" || key === "note") {
            changeTypes.add("notes");
          } else if (
            key === "videos" ||
            key === "video_urls" ||
            key === "videoUrls"
          ) {
            changeTypes.add("videos");
          }
        }
      });

      return Array.from(changeTypes);
    },
    [getFieldDisplay],
  );

  // Get icon for change type
  const getChangeTypeIcon = useCallback((type: string) => {
    const iconProps = { className: "w-3.5 h-3.5 text-gray-500" };

    switch (type) {
      case "price":
        return <DollarSign {...iconProps} />;
      case "status":
        return <CalendarCheck {...iconProps} />;
      case "image":
        return <ImageIcon {...iconProps} />;
      case "name":
        return <FileText {...iconProps} />;
      case "description":
        return <FileText {...iconProps} />;
      case "amenities":
        return <Grid {...iconProps} />;
      case "schedule":
        return <Clock {...iconProps} />;
      case "capacity":
        return <Users {...iconProps} />;
      case "area":
        return <Maximize2 {...iconProps} />;
      case "notes":
        return <StickyNote {...iconProps} />;
      case "videos":
        return <Video {...iconProps} />;
      case "approvalStatus":
        return <DoorClosed {...iconProps} />;
      default:
        return null;
    }
  }, []);

  // Get status badge
  const getStatusBadge = useCallback((status: PrivateOfficeUpdateStatus) => {
    const variants = {
      pending: {
        variant: "secondary" as const,
        color: "bg-yellow-100 text-yellow-800",
        icon: Clock,
      },
      approved: {
        variant: "default" as const,
        color: "bg-green-100 text-green-800",
        icon: Check,
      },
      rejected: {
        variant: "destructive" as const,
        color: "bg-red-100 text-red-800",
        icon: X,
      },
    };

    const config = variants[status];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={cn("gap-1", config.color)}>
        <Icon className="w-3 h-3" />
        {status === "pending"
          ? "Pendente"
          : status === "approved"
            ? "Aprovado"
            : "Reprovado"}
      </Badge>
    );
  }, []);

  // Get oldest request date from a group
  const getOldestRequestDate = useCallback(
    (requests: GroupedUpdateRequest["requests"]) => {
      if (requests.length === 0) return null;

      const dates = requests.map((request) =>
        new Date(request.created_at).getTime(),
      );
      const oldestTimestamp = Math.min(...dates);
      return new Date(oldestTimestamp);
    },
    [],
  );

  const resolveRoomImages = useCallback(
    (request: PrivateOfficeUpdateRequest): CurrentRoomImages => ({
      ...extractImagesFromOfficeInfo(request.office_info),
      ...(appliedImageSlots[request.office_info.id] ?? {}),
    }),
    [appliedImageSlots],
  );

  const handleImageSlotApplied = useCallback(
    (roomId: number, property: PictureProperty, url: string | null) => {
      setAppliedImageSlots((prev) => ({
        ...prev,
        [roomId]: { ...(prev[roomId] ?? {}), [property]: url },
      }));
    },
    [],
  );

  const updateDataAfterRequestChanged = (
    requestChanged: PrivateOfficeUpdateRequest,
  ) => {
    if (updateRequestsData) {
      const data =
        updateRequestsData.data.map((item) => {
          const index = item.requests.findIndex(
            (request) => request._id === requestChanged?._id,
          );
          if (index !== -1) {
            item.requests[index] =
              requestChanged ?? updateRequestsData?.data[0].requests[0];
          }

          return item;
        }) || [];

      setUpdateRequestsData({
        ...updateRequestsData,
        data: data,
      });
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto bg-white min-h-screen">
          <div className="px-6 py-6 border-b border-gray-100">
            <PageBreadcrumb title="Gerenciar Alterações" />
          </div>

          <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
            <div className="px-6 py-6">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Filtros
                  </h2>
                  {(statusFilter !== "all" || spaceFilter) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearAllFilters}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Limpar todos
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700">
                      Status das solicitações
                    </label>
                    <div className="inline-flex items-center bg-gray-100 rounded-full p-1 shadow-sm w-fit">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFilterChange("all")}
                        className={cn(
                          "h-8 px-4 rounded-full text-sm font-medium transition-all duration-200",
                          statusFilter === "all"
                            ? "bg-yellow-300 text-gray-800 hover:bg-yellow-400 shadow-sm"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50",
                        )}
                      >
                        Todos
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFilterChange("pending")}
                        className={cn(
                          "h-8 px-4 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2",
                          statusFilter === "pending"
                            ? "bg-yellow-300 text-gray-800 hover:bg-yellow-400 shadow-sm"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50",
                        )}
                      >
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        Pendente
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFilterChange("approved")}
                        className={cn(
                          "h-8 px-4 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2",
                          statusFilter === "approved"
                            ? "bg-yellow-300 text-gray-800 hover:bg-yellow-400 shadow-sm"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50",
                        )}
                      >
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Aprovado
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFilterChange("rejected")}
                        className={cn(
                          "h-8 px-4 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2",
                          statusFilter === "rejected"
                            ? "bg-yellow-300 text-gray-800 hover:bg-yellow-400 shadow-sm"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50",
                        )}
                      >
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        Reprovado
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700">
                      Nome do espaço
                    </label>
                    <div className="relative max-w-sm">
                      <Controller
                        control={control}
                        name="spaces"
                        render={({ field }) => (
                          <SearchInputCommand
                            name="spaces"
                            selectOption={(_name, item) => {
                              field.onChange(item);
                              setSpaceFilter(item);
                            }}
                            searchValue={field.value?.name || ""}
                            defaultValue={field.value?.name || ""}
                            optionsList={filterSpaces!}
                            updateList={handSelectedSpace}
                            label="Buscar por nome do espaço..."
                            inputWidth="w-full"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">
                  {groupedRequests.length === 0 &&
                    "Nenhum resultado encontrado"}
                  {groupedRequests.length > 0 && (
                    <>
                      <span className="font-medium text-gray-900">
                        {groupedRequests.length}
                      </span>{" "}
                      {groupedRequests.length === 1
                        ? "resultado"
                        : "resultados"}
                    </>
                  )}
                </span>

                <div className="flex items-center gap-2">
                  {statusFilter !== "all" && (
                    <span className="inline-flex items-center gap-1.5 text-xs bg-white border border-gray-200 px-2 py-1 rounded-md">
                      <div
                        className={cn(
                          "w-2 h-2 rounded-full",
                          statusFilter === "pending" && "bg-amber-400",
                          statusFilter === "approved" && "bg-green-500",
                          statusFilter === "rejected" && "bg-red-500",
                        )}
                      ></div>
                      {statusFilter === "pending" && "Pendente"}
                      {statusFilter === "approved" && "Aprovado"}
                      {statusFilter === "rejected" && "Reprovado"}
                    </span>
                  )}
                  {spaceFilter && (
                    <span className="text-xs bg-white border border-gray-200 px-2 py-1 rounded-md">
                      Espaço: &quot;{spaceFilter.name}&quot;
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-6 pb-24 bg-gray-50/30 relative">
            {isLoadingContent && (
              <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-10">
                <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-yellow-300 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm text-gray-600">
                      Atualizando...
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-4">
              {groupedRequests.map((room) => {
                const isRoomExpanded = expandedRooms.has(room._id);

                // Get room name from the first request (all requests in group have same office info)
                const roomName =
                  room.requests[0]?.office_info?.name || `Sala ${room._id}`;
                const spaceName = room.requests[0]?.space_info?.name;
                const cityName = room.requests[0]?.space_info?.cityname;
                const changeTypes = getChangeTypes(room.requests);
                const oldestDate = getOldestRequestDate(room.requests);

                return (
                  <Card
                    key={room._id}
                    className="shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <button
                            onClick={() => toggleRoomExpansion(room._id)}
                            className="flex items-start gap-3 text-left group flex-1 min-w-0"
                          >
                            <div className="pt-0.5">
                              {isRoomExpanded ? (
                                <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="mb-2">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                                    Sala
                                  </span>
                                  <CardTitle className="text-lg font-semibold text-gray-900 truncate">
                                    {roomName}
                                  </CardTitle>
                                </div>

                                <div className="flex items-center gap-4 text-sm">
                                  <div className="flex items-center gap-1.5 text-gray-500">
                                    <span className="text-xs text-gray-400">
                                      ID:
                                    </span>
                                    <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                                      {room._id}
                                    </span>
                                  </div>

                                  {spaceName && (
                                    <div className="flex items-center gap-1.5 text-gray-600 truncate">
                                      <span className="text-xs text-gray-400 flex-shrink-0">
                                        Espaço:
                                      </span>
                                      <span className="truncate font-medium">
                                        {spaceName}
                                      </span>
                                    </div>
                                  )}

                                  {cityName && (
                                    <div className="flex items-center gap-1.5 text-gray-500 flex-shrink-0">
                                      <span className="text-xs text-gray-400">
                                        Cidade:
                                      </span>
                                      <span className="text-sm font-medium">
                                        {cityName}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                <div className="flex items-center gap-2 mt-3">
                                  <span className="text-xs text-gray-400">
                                    Tipos de alteração:
                                  </span>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    {changeTypes.map((type) => {
                                      const typeLabels: Record<
                                        string,
                                        { label: string; title: string }
                                      > = {
                                        approvalStatus: {
                                          label: "Criação de sala",
                                          title:
                                            "Alteração de status de criação",
                                        },
                                        price: {
                                          label: "Preço",
                                          title: "Alteração de preço",
                                        },
                                        status: {
                                          label: "Disponibilidade",
                                          title: "Alteração de disponibilidade",
                                        },
                                        image: {
                                          label: "Imagem",
                                          title: "Alteração de imagem",
                                        },
                                        name: {
                                          label: "Nome",
                                          title: "Alteração de nome",
                                        },
                                        description: {
                                          label: "Descrição",
                                          title: "Alteração de descrição",
                                        },
                                        amenities: {
                                          label: "Comodidades",
                                          title: "Alteração de comodidades",
                                        },
                                        schedule: {
                                          label: "Horário",
                                          title:
                                            "Alteração de horário de funcionamento",
                                        },
                                        capacity: {
                                          label: "Capacidade",
                                          title: "Alteração de capacidade",
                                        },
                                        area: {
                                          label: "Área",
                                          title: "Alteração de área",
                                        },
                                        notes: {
                                          label: "Observações",
                                          title: "Alteração de observações",
                                        },
                                        videos: {
                                          label: "Vídeos",
                                          title: "Alteração de vídeos",
                                        },
                                      };

                                      const typeInfo = typeLabels[type] || {
                                        label: type,
                                        title: `Alteração de ${type}`,
                                      };

                                      return (
                                        <div
                                          key={type}
                                          className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-full"
                                          title={typeInfo.title}
                                        >
                                          {getChangeTypeIcon(type)}
                                          <span className="text-xs text-gray-600 capitalize">
                                            {typeInfo.label}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </button>
                        </div>

                        {oldestDate && (
                          <div className="flex-shrink-0 text-right">
                            <div className="text-xs text-gray-500 mb-1">
                              Solicitado em
                            </div>
                            <div className="text-sm font-medium text-gray-700">
                              {format(oldestDate, "dd/MM/yyyy")}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardHeader>

                    {isRoomExpanded && (
                      <CardContent className="pt-0">
                        <div className="space-y-3">
                          {room.requests.map((request) => {
                            // Use the new image request handler for image requests
                            if (isImageRequest(request.fields_to_update)) {
                              return (
                                <div key={request._id}>
                                  <ImageRequestHandler
                                    request={request}
                                    onApproved={updateDataAfterRequestChanged}
                                    onRejected={updateDataAfterRequestChanged}
                                    roomImages={resolveRoomImages(request)}
                                    onImageSlotApplied={handleImageSlotApplied}
                                  />
                                </div>
                              );
                            }

                            // Keep existing logic for non-image requests
                            const isRequestExpanded = expandedRequests.has(
                              request._id,
                            );
                            const fieldDisplay = getFieldDisplay(
                              request.fields_to_update,
                              request,
                            );

                            return (
                              <div
                                key={request._id}
                                className="border border-gray-200 rounded-lg bg-white p-4 hover:border-gray-300 transition-colors"
                              >
                                {/* Header da Alteração */}
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-start gap-3 flex-1">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                          {request.fields_to_update
                                            .approvalStatus
                                            ? "Solicitação de Criação"
                                            : "Solicitação de Alteração"}
                                        </span>
                                      </div>
                                      <h3 className="font-semibold text-gray-900 text-lg">
                                        {fieldDisplay?.name || "Campo"}
                                      </h3>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-2">
                                    {getStatusBadge(request.status)}
                                  </div>
                                </div>
                                {/* Display content based on request type */}
                                {request.fields_to_update.approvalStatus &&
                                request.current_inventory_office ? (
                                  /* Room Creation Request - Special Layout */
                                  <div className="space-y-6">
                                    {(() => {
                                      const transformedFields =
                                        transformInventoryOfficeToFields(
                                          request.current_inventory_office
                                            .metadata,
                                        );

                                      return Object.entries(transformedFields)
                                        .map(([key, value]) => {
                                          if (!value) return null;

                                          const fieldDisplay = getFieldDisplay(
                                            { [key]: value },
                                            request,
                                            true,
                                          );

                                          if (!fieldDisplay) return null;

                                          const isAmenities =
                                            key === "amenities";
                                          const isPhotos = key === "photos";

                                          return (
                                            <div
                                              key={key}
                                              className="space-y-2"
                                            >
                                              <h3 className="font-semibold text-gray-900 text-lg">
                                                {fieldDisplay.name}
                                              </h3>
                                              <div className="bg-gray-50 p-3 rounded border-l-4 border-orange-400">
                                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                                                  PROPOSTO
                                                </span>
                                                {isPhotos &&
                                                Array.isArray(value) ? (
                                                  <div className="space-y-2">
                                                    <span className="text-sm font-medium text-gray-900 block">
                                                      {fieldDisplay.value}
                                                    </span>
                                                    <div className="max-h-64 overflow-y-auto">
                                                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                                        {value.map(
                                                          (
                                                            photo: PhotoItem,
                                                            photoIndex: number,
                                                          ) => (
                                                            <div
                                                              key={photoIndex}
                                                              className="relative aspect-square"
                                                            >
                                                              <img
                                                                src={photo.url}
                                                                alt={`Foto ${photoIndex + 1}`}
                                                                className="w-full h-full object-cover rounded border border-gray-200"
                                                                loading="lazy"
                                                                onError={(
                                                                  e,
                                                                ) => {
                                                                  const target =
                                                                    e.target as HTMLImageElement;
                                                                  target.style.display =
                                                                    "none";
                                                                }}
                                                              />
                                                              {photo.tags &&
                                                                photo.tags
                                                                  .length >
                                                                  0 && (
                                                                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b">
                                                                    {photo.tags.join(
                                                                      ", ",
                                                                    )}
                                                                  </div>
                                                                )}
                                                            </div>
                                                          ),
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <span
                                                    className={`text-sm font-medium text-gray-900 ${isAmenities ? "" : "break-words"}`}
                                                  >
                                                    {fieldDisplay.value}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })
                                        .filter(Boolean);
                                    })()}

                                    <div className="mt-4 pt-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
                                      <div>
                                        <span className="font-medium">ID:</span>{" "}
                                        {request.office_info?.id}
                                      </div>
                                      <div>
                                        <span className="font-medium">
                                          Espaço:
                                        </span>{" "}
                                        {room.requests[0]?.space_info?.name}
                                      </div>
                                      <div>
                                        <span className="font-medium">
                                          Cidade:
                                        </span>{" "}
                                        {room.requests[0]?.space_info?.cityname}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  /* Regular Update Request - Standard Layout */
                                  (() => {
                                    const prevDisplay = request.previous_values
                                      ? getFieldDisplay(
                                          request.previous_values,
                                          request,
                                          false, // isProposed = false for previous values
                                        )
                                      : null;

                                    return (
                                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
                                        <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-gray-300">
                                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                                            ATUAL
                                          </div>
                                          <div className="text-sm font-medium text-gray-700 break-words">
                                            {prevDisplay?.value ||
                                              "Sem valor anterior"}
                                          </div>
                                        </div>

                                        <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-yellow-300">
                                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                                            PROPOSTO
                                          </div>
                                          <div className="text-sm font-medium text-gray-900 break-words">
                                            {fieldDisplay?.value || "-"}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })()
                                )}

                                <div className="flex items-center justify-between">
                                  <button
                                    onClick={() =>
                                      toggleRequestExpansion(request._id)
                                    }
                                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                                  >
                                    {isRequestExpanded ? (
                                      <>
                                        <ChevronDown className="w-4 h-4" />
                                        <span>Ocultar detalhes</span>
                                      </>
                                    ) : (
                                      <>
                                        <ChevronRight className="w-4 h-4" />
                                        <span>Ver detalhes</span>
                                      </>
                                    )}
                                  </button>

                                  {request.status === "pending" && (
                                    <div className="flex items-center gap-2">
                                      <Button
                                        size="sm"
                                        onClick={() =>
                                          handleReject(request._id)
                                        }
                                        disabled={isApproving}
                                        loading={isLoadingRejecting}
                                        variant="outline"
                                        className="h-8 px-3 text-xs border-red-200 text-red-600 hover:bg-red-50"
                                      >
                                        <X className="w-3 h-3 mr-1" />
                                        Rejeitar
                                      </Button>
                                      <Button
                                        size="sm"
                                        onClick={() =>
                                          handleApprove(request._id)
                                        }
                                        disabled={isApproving}
                                        loading={isLoadingApproving}
                                        className="bg-green-600 hover:bg-green-700 h-8 px-3 text-xs"
                                      >
                                        <Check className="w-3 h-3 mr-1" />
                                        Aprovar
                                      </Button>
                                    </div>
                                  )}
                                </div>

                                {isRequestExpanded && (
                                  <div className="mt-4 pt-4 border-t border-gray-200">
                                    <h4 className="text-sm font-medium text-gray-700 mb-4">
                                      Fluxo de aprovação
                                    </h4>
                                    <div className="flex items-start justify-between mb-6">
                                      <div className="flex flex-col items-center flex-1">
                                        <div
                                          className={cn(
                                            "w-8 h-8 rounded-full flex items-center justify-center",
                                            "bg-green-100 text-green-600",
                                          )}
                                        >
                                          <Check className="w-4 h-4" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-900 mt-2">
                                          {request.fields_to_update
                                            .approvalStatus
                                            ? "Criação de sala"
                                            : "Edição da sala"}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {format(
                                            new Date(request.created_at),
                                            "dd/MM/yyyy",
                                          )}
                                        </span>
                                      </div>

                                      <div className="flex-1 flex items-center pt-4">
                                        <div
                                          className={cn(
                                            "h-0.5 w-full",
                                            request.status === "pending"
                                              ? "bg-gray-200"
                                              : "bg-green-200",
                                          )}
                                        />
                                      </div>

                                      <div className="flex flex-col items-center flex-1">
                                        <div
                                          className={cn(
                                            "w-8 h-8 rounded-full flex items-center justify-center",
                                            request.status === "pending"
                                              ? "bg-yellow-100 text-yellow-600"
                                              : "bg-green-100 text-green-600",
                                          )}
                                        >
                                          {request.status === "pending" ? (
                                            <Clock className="w-4 h-4" />
                                          ) : (
                                            <Check className="w-4 h-4" />
                                          )}
                                        </div>
                                        <span className="text-sm font-medium text-gray-900 mt-2">
                                          Pendente de aprovação
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {format(
                                            new Date(request.created_at),
                                            "dd/MM/yyyy",
                                          )}
                                        </span>
                                        {request.status === "pending" && (
                                          <Badge
                                            variant="outline"
                                            className="mt-1 text-xs bg-green-50 text-green-700 border-green-200"
                                          >
                                            Dentro do prazo
                                          </Badge>
                                        )}
                                        <span className="text-xs text-gray-500 mt-1">
                                          {request.status === "pending"
                                            ? "Aguardando análise do time."
                                            : "Análise concluída."}
                                        </span>
                                      </div>

                                      <div className="flex-1 flex items-center pt-4">
                                        <div
                                          className={cn(
                                            "h-0.5 w-full",
                                            request.status === "pending"
                                              ? "bg-gray-200"
                                              : request.status === "approved"
                                                ? "bg-green-200"
                                                : "bg-red-200",
                                          )}
                                        />
                                      </div>

                                      <div className="flex flex-col items-center flex-1">
                                        <div
                                          className={cn(
                                            "w-8 h-8 rounded-full flex items-center justify-center",
                                            request.status === "pending"
                                              ? "bg-gray-100 text-gray-400"
                                              : request.status === "approved"
                                                ? "bg-green-100 text-green-600"
                                                : "bg-red-100 text-red-600",
                                          )}
                                        >
                                          {request.status === "pending" ? (
                                            <span className="text-sm font-medium">
                                              3
                                            </span>
                                          ) : request.status === "approved" ? (
                                            <Check className="w-4 h-4" />
                                          ) : (
                                            <X className="w-4 h-4" />
                                          )}
                                        </div>
                                        <span
                                          className={cn(
                                            "text-sm font-medium mt-2",
                                            request.status === "pending"
                                              ? "text-gray-400"
                                              : "text-gray-900",
                                          )}
                                        >
                                          Resultado da revisão
                                        </span>
                                        {request.status !== "pending" &&
                                          request.reviewed_at && (
                                            <span className="text-xs text-gray-500">
                                              {format(
                                                new Date(request.reviewed_at),
                                                "dd/MM/yyyy",
                                              )}
                                            </span>
                                          )}
                                        <span
                                          className={cn(
                                            "text-xs mt-1",
                                            request.status === "pending"
                                              ? "text-gray-400"
                                              : "text-gray-500",
                                          )}
                                        >
                                          {request.status === "pending"
                                            ? "O resultado será exibido aqui após a revisão."
                                            : request.status === "approved"
                                              ? "Alteração aprovada."
                                              : "Alteração rejeitada."}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                      <div className="space-y-2 text-sm">
                                        <div className="flex">
                                          <span className="font-medium text-gray-700 w-28">
                                            Etapa atual:
                                          </span>
                                          <span className="text-gray-600">
                                            {request.status === "pending"
                                              ? "Pendente de aprovação"
                                              : request.status === "approved"
                                                ? "Aprovado"
                                                : "Reprovado"}
                                          </span>
                                        </div>
                                        <div className="flex">
                                          <span className="font-medium text-gray-700 w-28">
                                            Editado por:
                                          </span>
                                          <span className="text-gray-600">
                                            {request.requested_by.username}
                                          </span>
                                        </div>
                                      </div>

                                      <div className="mt-4 pt-4 border-t border-gray-200">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() =>
                                            navigate(
                                              `/private-office/edit/${room._id}`,
                                            )
                                          }
                                          className="h-9 px-4"
                                        >
                                          <Pencil className="w-4 h-4 mr-2" />
                                          Editar sala
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}

              {groupedRequests.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <div className="flex flex-col items-center space-y-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          Nenhum resultado encontrado
                        </h3>
                        <p className="text-sm text-gray-500 max-w-sm">
                          Tente ajustar os filtros ou limpar a busca para ver
                          mais solicitações
                        </p>
                      </div>

                      {(statusFilter !== "all" || spaceFilter) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleClearAllFilters}
                          className="mt-4"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Limpar filtros
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>

        {updateRequestsData?.pagination && (
          <div className="fixed bottom-0 left-0 right-0 z-30">
            <div className="max-w-6xl mx-auto bg-white border-t border-gray-200 shadow-lg">
              <Pagination
                page={currentPage}
                totalPages={updateRequestsData.pagination.totalPages}
                count={updateRequestsData.pagination.total}
                onPageClick={handlePageClick}
                onNextPageClick={handleNextPageClick}
                onPrevPageClick={handlePrevPageClick}
                loading={isLoadingContent}
                pageName="Alterações"
                totalLines={`${updateRequestsData.pagination.total} ${
                  updateRequestsData.pagination.total === 1 ? "item" : "itens"
                }`}
              />
            </div>
          </div>
        )}

        <Outlet />

        {fullSizeImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
            onClick={closeImageModal}
          >
            <div className="relative max-w-5xl max-h-[90vh] w-full h-full flex items-center justify-center">
              <img
                src={fullSizeImage.src}
                alt={fullSizeImage.alt}
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={closeImageModal}
                className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-opacity"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
